import React, { Component } from 'react';
import { connect } from 'react-redux';
import { hashHistory } from 'react-router';
import ButtonInc from '../../../../../shared-ui/ButtonInc';
import MemberSelectionTable from './Components/MemberSelectionTable';
import SelectRelationshipModal from './Components/SelectRelationshipModal';
import AddMemberFormModal from './Components/AddMemberFormModal';
import PlanSelectionModal from './Components/PlanSelectionModal';
import SummaryScreen from './Components/SummaryScreen';
import AmendmentProgressDialog from '../shared/Components/AmendmentProgressDialog';
import AlertDialog from '../../../../../components/AlertDialog/index.es6';
import PolicyInfoHeader from '../shared/Components/PolicyInfoHeader';
import AmendmentPageHeader from '../shared/Components/AmendmentPageHeader';
import { extractMembersFromPolicy, groupMembers } from './utils/memberHelpers';
import { buildRemovalQuotation } from './utils/premiumCalculator';
import { processRemovalAmendment } from './utils/amendmentApi';
import { calculateGroupTotal } from './utils/calculations';
import { getAllProducts } from '../../../../../lib/requests.es6';

class ManageMembersScreen extends Component {
  constructor(props) {
    super(props);

    const allMembers = extractMembersFromPolicy(props.policy);
    const grouped = groupMembers(allMembers);

    this.state = {
      allMembers,
      groupedMembers: grouped,
      removedUuids: [],
      addedMembers: [],
      showRelationshipSelector: false,
      showAddMemberForm: false,
      showPlanSelectionModal: false,
      selectedMemberType: null,
      selectedRelationship: null,
      showParentPlanWarning: false,
      selectedParentPlan: null,
      premiumCalculation: null,
      // Amendment progress tracking
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSummary: false,
      showSuccessDialog: false,
      products: props.products || [],
      loadingProducts: false
    };
  }

  componentDidMount() {
    const { products } = this.props;

    // Fetch products if not available from Redux
    if (!products || products.length === 0) {
      this.fetchProducts();
    }
  }

  fetchProducts = async () => {
    this.setState({ loadingProducts: true });

    try {
      const response = await getAllProducts();
      const products = response.data || [];
      this.setState({ products, loadingProducts: false });
    } catch (error) {
      console.error('Error fetching products:', error);
      this.setState({ loadingProducts: false });
    }
  };

  handleRemoveMember = (memberUuid) => {
    this.setState(prevState => ({
      removedUuids: [...prevState.removedUuids, memberUuid]
    }));
  };

  handleReinstateMember = (memberUuid) => {
    this.setState(prevState => ({
      removedUuids: prevState.removedUuids.filter(uuid => uuid !== memberUuid)
    }));
  };

  getMembersToRemove = () => {
    const { removedUuids, allMembers } = this.state;
    return allMembers.filter(m => removedUuids.includes(m.uuid));
  };

  getRemainingMembers = () => {
    const { removedUuids, allMembers } = this.state;
    return allMembers.filter(m => !removedUuids.includes(m.uuid));
  };

  getAddedMembers = () => {
    return this.state.addedMembers;
  };

  getAllFinalMembers = () => {
    return [...this.getRemainingMembers(), ...this.state.addedMembers];
  };

  handleOpenAddMemberSelector = (memberType) => {
    this.setState({
      showRelationshipSelector: true,
      selectedMemberType: memberType
    });
  };

  handleCloseRelationshipSelector = () => {
    this.setState({
      showRelationshipSelector: false,
      selectedMemberType: null
    });
  };

  handleSelectRelationship = (relationship) => {
    // Check if this is a parent relationship
    const isParentRelationship = relationship === 'Parent' || relationship === 'Parent_In_Law';
    const { selectedParentPlan, allMembers, addedMembers } = this.state;

    if (isParentRelationship) {
      // Check if there are already parent members in the policy or newly added
      const existingParents = allMembers.filter(m =>
        m.relationship === 'Parent' || m.relationship === 'Parent_In_Law'
      );
      const addedParents = addedMembers.filter(m =>
        m.relationship === 'Parent' || m.relationship === 'Parent_In_Law'
      );

      const hasExistingParents = existingParents.length > 0 || addedParents.length > 0;

      if (hasExistingParents) {
        // Parent members already exist, use their plan (don't show warning)
        this.setState({
          showRelationshipSelector: false,
          showAddMemberForm: true,
          selectedRelationship: relationship
        });
      } else if (selectedParentPlan === null) {
        // This is the FIRST parent being added, show plan selection warning
        this.setState({
          showRelationshipSelector: false,
          showParentPlanWarning: true,
          selectedRelationship: relationship
        });
      } else {
        // Parent plan already selected for this session
        this.setState({
          showRelationshipSelector: false,
          showAddMemberForm: true,
          selectedRelationship: relationship
        });
      }
    } else {
      // Not a parent relationship, proceed normally
      this.setState({
        showRelationshipSelector: false,
        showAddMemberForm: true,
        selectedRelationship: relationship
      });
    }
  };

  handleContinueWithCurrentPlan = () => {
    const { policy } = this.props;

    // Get the nuclear plan code
    const nuclearPlanCode = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.primary_plan
      : null;

    // Store the nuclear plan code as the parent plan choice
    this.setState({
      showParentPlanWarning: false,
      selectedParentPlan: nuclearPlanCode, // Store actual plan code
      showAddMemberForm: true
    });
  };

  handleChangePlan = () => {
    // Show plan selection modal to let user choose a different plan
    this.setState({
      showParentPlanWarning: false,
      showPlanSelectionModal: true
    });
  };

  handleClosePlanSelectionModal = () => {
    this.setState({
      showPlanSelectionModal: false
    });
  };

  handleSelectParentPlan = (selectedPlan) => {
    // Store the selected plan code and show add member form
    this.setState({
      showPlanSelectionModal: false,
      selectedParentPlan: selectedPlan.code, // Store the plan code
      showAddMemberForm: true
    });
  };

  handleCloseAddMemberForm = () => {
    this.setState({
      showAddMemberForm: false,
      selectedRelationship: null,
      selectedMemberType: null,
      selectedParentPlan: null // Reset parent plan selection when closing
    });
  };

  handleAddMember = (newMember) => {
    this.setState(prevState => ({
      addedMembers: [...prevState.addedMembers, newMember],
      showAddMemberForm: false,
      selectedRelationship: null,
      selectedMemberType: null
    }));
  };

  handleCancelAddedMember = (memberUuid) => {
    this.setState(prevState => ({
      addedMembers: prevState.addedMembers.filter(m => m.uuid !== memberUuid)
    }));
  };

  handleNext = () => {
    this.setState({ showSummary: true });
  };

  handleBackFromSummary = () => {
    this.setState({ showSummary: false });
  };

  handleConfirmChanges = async () => {
    const { policy } = this.props;
    const { addedMembers } = this.state;
    const finalMembers = this.getAllFinalMembers(); // remaining + added
    const membersToRemove = this.getMembersToRemove();

    // Calculate premium for API call
    const allMembersGrouped = groupMembers(this.state.allMembers);
    const oldGrandTotal = calculateGroupTotal(allMembersGrouped.nuclear) +
                          calculateGroupTotal(allMembersGrouped.parents) +
                          calculateGroupTotal(allMembersGrouped.extended);

    const finalGrouped = groupMembers(finalMembers);
    const newGrandTotal = calculateGroupTotal(finalGrouped.nuclear) +
                          calculateGroupTotal(finalGrouped.parents) +
                          calculateGroupTotal(finalGrouped.extended);

    const premiumCalculation = {
      oldTotal: oldGrandTotal / 100,
      newTotal: newGrandTotal / 100,
      change: (newGrandTotal - oldGrandTotal) / 100,
      coverageTypeChange: null
    };

    // Reset step statuses
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSuccessDialog: false
    });

    try {
      // Build quotation payload with final members (remaining + added)
      const quotationPayload = buildRemovalQuotation(
        policy,
        finalMembers,
        premiumCalculation.coverageTypeChange,
        addedMembers
      );

      // Process the amendment with status callbacks
      const updateStatus = (step, status) => {
        // Update step status (loading, success, error)
        this.setState(prevState => ({
          steps: { ...prevState.steps, [step]: status }
        }));
      };

      const result = await processRemovalAmendment(
        quotationPayload,
        policy,
        membersToRemove,
        addedMembers,
        updateStatus
      );

      // Amendment succeeded (even if note failed)
      this.setState({
        showSuccessDialog: true,
        noteError: result.noteError || null
      });
    } catch (error) {
      console.error('Failed to process amendment:', error);
      const errorMessage = error.message || 'Failed to process amendment. Please try again.';

      // Determine which step failed
      this.setState(prevState => {
        const quoteError = prevState.steps.quote === 'error' ? errorMessage : null;
        const swapError = prevState.steps.swap === 'error' ? errorMessage : null;

        return { quoteError, swapError };
      });
    }
  };

  handleSuccess = () => {
    const { policy } = this.props;
    this.setState({ showSuccessDialog: false });
    // Navigate to policy processing page
    hashHistory.push(`admin/policy-processing/${policy.policy_number}`);
  };

  handleRetry = () => {
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null
    });
  };

  handleCancel = () => {
    if (this.props.onBack) {
      this.props.onBack();
    }
  };

  renderSummaryScreen() {
    const { steps, quoteError, swapError, noteError } = this.state;
    const membersToRemove = this.getMembersToRemove();

    return (
      <div>
        <SummaryScreen
          policy={this.props.policy}
          allMembers={this.state.allMembers}
          membersToRemove={membersToRemove}
          membersToAdd={this.state.addedMembers}
          remainingMembers={this.getRemainingMembers()}
          finalMembers={this.getAllFinalMembers()}
          onBack={this.handleBackFromSummary}
          onConfirm={this.handleConfirmChanges}
        />

        {/* Amendment Progress Dialog */}
        <AmendmentProgressDialog
          steps={steps}
          quoteError={quoteError}
          swapError={swapError}
          noteError={noteError}
          successMessage={`The policy has been successfully updated. ${membersToRemove.length} member(s) have been ${membersToRemove.length > 0 ? 'removed from' : 'added to'} the policy.`}
          onViewPolicy={this.handleSuccess}
          onRetry={this.handleRetry}
        />
      </div>
    );
  }

  render() {
    const { policy } = this.props;
    const {
      allMembers,
      removedUuids,
      addedMembers,
      products,
      showRelationshipSelector,
      showAddMemberForm,
      showPlanSelectionModal,
      selectedMemberType,
      selectedRelationship,
      showParentPlanWarning,
      selectedParentPlan,
      steps,
      quoteError,
      swapError,
      noteError,
      showSummary
    } = this.state;

    const membersToRemove = this.getMembersToRemove();
    const remainingMembers = this.getRemainingMembers();
    const canProceed = removedUuids.length > 0 || addedMembers.length > 0;

    // Get coverage type from policy
    const coverageType = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.cover_type
      : 'Member & Family';
    const isMemberOnly = coverageType === 'Member Only';

    // Check if amendment is in progress
    const isProcessing = steps.quote === 'loading' || steps.swap === 'loading' || steps.note === 'loading';

    // Get plans from policy
    const nuclearPlanCode = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.primary_plan
      : null;
    const parentPlanCode = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.secondary_plan
      : null;

    // Find plan products
    const nuclearPlan = products && products.length > 0
      ? products.find(p => p.code === nuclearPlanCode)
      : null;

    // Determine parent plan based on existing parents or user's choice
    let parentPlan = null;

    // Check if there are existing parent members in the policy
    const existingParents = allMembers.filter(m =>
      m.relationship === 'Parent' || m.relationship === 'Parent_In_Law'
    );

    if (existingParents.length > 0) {
      // Use the existing parent plan from the policy
      if (parentPlanCode && products && products.length > 0) {
        parentPlan = products.find(p => p.code === parentPlanCode);
      } else {
        // If no parent plan code in policy, they're on the same plan as nuclear
        parentPlan = nuclearPlan;
      }
    } else if (selectedParentPlan && products && products.length > 0) {
      // User already selected a plan for parents in this session - use that plan code
      parentPlan = products.find(p => p.code === selectedParentPlan);
    } else {
      // Default to nuclear plan if no parent plan specified
      parentPlan = nuclearPlan;
    }

    // Display only remaining members (not removed) + added members
    const displayGrouped = groupMembers(remainingMembers);

    // Merge added members into their respective groups
    const addedGrouped = groupMembers(addedMembers);
    const mergedNuclear = [...displayGrouped.nuclear, ...addedGrouped.nuclear];
    const mergedParents = [...displayGrouped.parents, ...addedGrouped.parents];
    const mergedExtended = [...displayGrouped.extended, ...addedGrouped.extended];

    // Calculate totals by summing member.premium_amount_in_cents
    const nuclearTotal = calculateGroupTotal(displayGrouped.nuclear);
    const parentsTotal = calculateGroupTotal(displayGrouped.parents);
    const extendedTotal = calculateGroupTotal(displayGrouped.extended);

    // Calculate added members total
    const addedMembersTotal = calculateGroupTotal(addedMembers);

    // Grand total includes remaining + added members
    const grandTotal = nuclearTotal + parentsTotal + extendedTotal + addedMembersTotal;

    // Show summary screen if in summary mode
    if (showSummary) {
      return this.renderSummaryScreen();
    }

    return (
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Unified Header */}
        <AmendmentPageHeader
          title="Manage Members"
          subtitle="Add or remove policy members. Premiums update in real-time."
          onBack={this.handleCancel}
        />

        {/* Policy Information */}
        <PolicyInfoHeader policy={policy} showTotalPremium={true} totalPremium={grandTotal} />

        {/* Member Tables */}
        <MemberSelectionTable
          members={mergedNuclear}
          onRemove={this.handleRemoveMember}
          onAdd={() => this.handleOpenAddMemberSelector('nuclear')}
          onCancelAdded={this.handleCancelAddedMember}
          title="Nuclear Members"
          showTotal={true}
          totalPremium={nuclearTotal + calculateGroupTotal(addedGrouped.nuclear)}
          showAddButton={!isMemberOnly}
          addedMemberUuids={addedMembers.map(m => m.uuid)}
        />

        <MemberSelectionTable
          members={mergedParents}
          onRemove={this.handleRemoveMember}
          onAdd={() => this.handleOpenAddMemberSelector('parent')}
          onCancelAdded={this.handleCancelAddedMember}
          title="Parent Members"
          showTotal={true}
          totalPremium={parentsTotal + calculateGroupTotal(addedGrouped.parents)}
          showAddButton={true}
          addedMemberUuids={addedMembers.map(m => m.uuid)}
        />

        <MemberSelectionTable
          members={mergedExtended}
          onRemove={this.handleRemoveMember}
          onAdd={() => this.handleOpenAddMemberSelector('extended')}
          onCancelAdded={this.handleCancelAddedMember}
          title="Extended Members"
          showTotal={true}
          totalPremium={extendedTotal + calculateGroupTotal(addedGrouped.extended)}
          showAddButton={true}
          addedMemberUuids={addedMembers.map(m => m.uuid)}
        />

        {/* Removed Members Table */}
        {membersToRemove.length > 0 && (
          <MemberSelectionTable
            members={membersToRemove}
            onRemove={this.handleReinstateMember}
            title="Removed Members"
            showTotal={false}
            isRemovedTable={true}
          />
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '30px' }}>
          <ButtonInc
            onClick={this.handleNext}
            disabled={!canProceed || isProcessing}
            style={{
              background: !canProceed || isProcessing ? '#ccc' : '#032A37',
              opacity: !canProceed || isProcessing ? 0.6 : 1
            }}
          >
            Next
          </ButtonInc>
        </div>

        {/* Amendment Progress Dialog */}
        <AmendmentProgressDialog
          steps={steps}
          quoteError={quoteError}
          swapError={swapError}
          noteError={noteError}
          successMessage={`The policy has been successfully updated. ${membersToRemove.length} member(s) have been removed from the policy.`}
          onViewPolicy={this.handleSuccess}
          onRetry={this.handleRetry}
        />

        {/* Select Relationship Modal */}
        <SelectRelationshipModal
          show={showRelationshipSelector}
          onClose={this.handleCloseRelationshipSelector}
          onSelectRelationship={this.handleSelectRelationship}
          memberType={selectedMemberType}
          currentMembers={remainingMembers}
          addedMembers={addedMembers}
          nuclearPlan={nuclearPlan}
          parentPlan={parentPlan}
          policy={policy}
        />

        {/* Parent Plan Selection Warning */}
        <AlertDialog
          warning
          show={showParentPlanWarning}
          size="sm"
          title="Would you like to select new plan for parents?"
          style={{ marginTop: '0', top: '50%', transform: 'translateY(-50%)' }}
          onConfirm={this.handleContinueWithCurrentPlan}
          onCancel={this.handleChangePlan}
          confirmBtnText="Continue with current plan"
          cancelBtnText="Change Plan"
          showCancel={true}
          showConfirm={true}
        >
        </AlertDialog>

        {/* Add Member Form Modal */}
        <AddMemberFormModal
          show={showAddMemberForm}
          onClose={this.handleCloseAddMemberForm}
          onAddMember={this.handleAddMember}
          relationship={selectedRelationship}
          memberType={selectedMemberType}
          nuclearPlan={nuclearPlan}
          parentPlan={parentPlan}
          policy={policy}
          currentMembers={remainingMembers}
          addedMembers={addedMembers}
        />

        {/* Plan Selection Modal */}
        <PlanSelectionModal
          show={showPlanSelectionModal}
          onClose={this.handleClosePlanSelectionModal}
          onSelectPlan={this.handleSelectParentPlan}
          plans={products}
          nuclearPlan={nuclearPlan}
          coverType={coverageType}
        />
      </div>
    );
  }
}

export default connect((state) => ({
  products: state.customerScreen ? state.customerScreen.products : []
}))(ManageMembersScreen);
